import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, Row, Col, Button, Spin, Table, DatePicker, Empty, Statistic, Alert, 
  Space, Divider, Progress, Typography, Tag, List, Tooltip, message,
  Tabs, Select, Breadcrumb, Input,
  Dropdown,
  Layout,
  Menu,
  Modal,
  Popover,
  Radio,
  Switch,
  Calendar,
  Checkbox
} from 'antd';
import { 
  DownloadOutlined, CalendarOutlined, FileExcelOutlined,
  CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined,
  CarOutlined, ClockCircleOutlined, ScheduleOutlined,
  WarningOutlined, FieldTimeOutlined, BarChartOutlined,
  PieChartOutlined, LineChartOutlined, HeatMapOutlined,
  ImportOutlined, ArrowLeftOutlined, HomeOutlined,
  LeftOutlined, RightOutlined, BulbOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from '../../dayjs';
import OTRCharts from './components/OTRCharts';
import OTRImporter from './components/OTRImporter';
import OTRPerformanceRadialChart from './components/OTRPerformanceRadialChart';
import OTRMultiTripChart from './components/OTRMultiTripChart';
import { 
  analyzeTripPerformance, 
  analyzeStopPerformance, 
  analyzeDriverPerformance,
  generateSummaryInsights,
  TIME_PERIODS
} from './utils/OTRDashboardAnalytics';
import OTRDataService from './utils/OTRDataService';
import { generateSampleOTRData } from './utils/generateTestData';
import OTRTripDetailView from './components/OTRTripDetailView';
import OTRStatisticalVarianceChart from './components/OTRStatisticalVarianceChart';
import OTRDateRangePicker from './components/OTRDateRangePicker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ReferenceLine } from 'recharts';
import { ResponsiveContainer } from 'recharts';
import OTRDailyOverviewTab from './components/OTRDailyOverviewTab';
import OTRInsightCard from './components/OTRInsightCard';
import { 
  analyzeTripWithRuleset, 
  analyzeStopWithRuleset, 
  analyzeDriverWithRuleset,
  analyzeDailyPerformanceWithRuleset
} from './utils/OTRKPIAIRuleset';
import { OTR_THRESHOLDS } from './utils/OTRAnalytics';

const { TabPane } = Tabs;
const { Text, Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// View level constants
const VIEW_LEVELS = {
  TRIP_AGGREGATE: 1,   // Level 1: Aggregate trip performance
  TRIP_BY_DATE: 2,     // Level 2: Trip performance by date
  TRIP_DETAIL: 3,      // Level 3: Trip detail (stop-by-stop)
  STOP_DETAIL: 4       // Level 4: Stop detail (showing trips for a specific stop)
};

// Global debug function for the browser console
window.checkBusableStopTSN = async (tsn) => {
  try {
    return await OTRDataService.checkTSNExists(tsn);
  } catch (error) {
    console.error('Error checking TSN:', error);
    return false;
  }
};

const OTRDashboard = () => {
  /**
   * OTR Dashboard provides a three-level hierarchical view of OTR performance:
   * 
   * Level 1: Trip Aggregate - Shows aggregate trip performance across the selected time period
   *   - Each row represents a unique trip with aggregate performance metrics
   *   - No date column (time period is in the header)
   * 
   * Level 2: Trip by Date - Shows performance for a specific trip broken down by date
   *   - Each row represents the same trip on a different date
   *   - Date column is displayed
   *   - Shows first/mid/last stop OTR metrics for each date
   * 
   * Level 3: Trip Detail - Shows all stops for a specific trip on a specific date
   *   - Each row represents a stop in the trip's stopping pattern
   *   - Displays stops in sequence order
   *   - Shows OTR performance for each stop
   */
  const [loading, setLoading] = useState(true);
  const [dateRange, _setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs().endOf('day')]);
  const setDateRange = (range) => {
    if (Array.isArray(range) && range.length === 2 && range[1]) {
      _setDateRange([range[0], range[1].endOf('day')]);
    } else {
      _setDateRange(range);
    }
  };
  const [selectedDatePreset, setSelectedDatePreset] = useState('last30');
  const [tripData, setTripData] = useState([]);
  const [viewLevel, setViewLevel] = useState(VIEW_LEVELS.TRIP_AGGREGATE);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [showImporter, setShowImporter] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Filter states
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('all');
  const [stopSearchText, setStopSearchText] = useState('');
  
  // Available filter options (populated after data load)
  const [filterOptions, setFilterOptions] = useState({
    routes: [],
    drivers: [],
    depots: [],
    operatorIds: [],
    contracts: []
  });

  // Additional data states
  const [stopData, setStopData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    insights: [],
    recommendations: []
  });

  // In the state declarations section, after other state variables
  const [selectedStopTypes, setSelectedStopTypes] = useState(['F']); // Default to "First" stops

  // Add a utility function to filter data by stop type
  const filterDataByStopType = useCallback((data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    // When 'ALL' is selected, return all the data without filtering
    if (selectedStopTypes.includes('ALL')) return data;
    
    return data.filter(record => {
      if (!record.TSN_FIRST_LAST_IND) return false;
      return selectedStopTypes.includes(record.TSN_FIRST_LAST_IND);
    });
  }, [selectedStopTypes]);

  // Simplify date range handling for the new component
  const handleDateRangeChange = (dates) => {
    if (dates && Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      console.log("Setting date range:", dates[0].format('YYYY-MM-DD'), 'to', dates[1].format('YYYY-MM-DD'));
      
      // If the dates match any of our presets, set the selectedDatePreset value
      const today = dayjs();
      const dateStart = dates[0];
      const dateEnd = dates[1];
      
      // Check for "Last 30 days"
      const thirtyDaysAgo = today.clone().subtract(30, 'day').startOf('day');
      if (dateStart.isSame(thirtyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last30');
        return;
      }
      
      // Check for "Last 60 days"
      const sixtyDaysAgo = today.clone().subtract(60, 'day').startOf('day');
      if (dateStart.isSame(sixtyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last60');
        return;
      }
      
      // Check for "Last 90 days"
      const ninetyDaysAgo = today.clone().subtract(90, 'day').startOf('day');
      if (dateStart.isSame(ninetyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last90');
        return;
      }
      
      // This is a custom range
      setSelectedDatePreset('custom');
    }
  };

  // Add useEffect to set the default date range on component mount
  useEffect(() => {
    // Set default to "Last 30 days"
    const today = dayjs().endOf('day');
    const thirtyDaysAgo = today.subtract(30, 'day');
    setDateRange([thirtyDaysAgo, today]);
    
    // Log for debugging
    console.log('Setting initial date range:', thirtyDaysAgo.format('YYYY-MM-DD'), 'to', today.format('YYYY-MM-DD'));
  }, []);

  // Load data when component mounts or when filters change
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
    loadData();
    }
  }, [dateRange, selectedRoute, selectedDriver, selectedDepot, selectedOperatorId, selectedContract, selectedTimePeriod]);

  // Initial check for data in storage
  // useEffect(() => {
  //     const loadData = async () => {
  //           // Check if we have data in storage when component mounts
  //       const metadata = OTRDataService.getMetadata();
  //       if (metadata) {
  //         message.info(`Found OTR data in storage: ${metadata.totalRecords} records from ${metadata.dateRange.start} to ${metadata.dateRange.end}`);
          
  //         // Update filter options from metadata
  //         setFilterOptions(prevOptions => ({
  //           ...prevOptions,
  //           routes: metadata.routes || [],
  //           drivers: metadata.drivers || [],
  //           depots: metadata.depots || [],
  //           operatorIds: metadata.operatorIds || [],
  //           contracts: metadata.contracts || []
  //         }));
          
  //         // Get all data to ensure we have all routes
  //         const allData = await OTRDataService.retrieveAllData();
  //         if (allData && allData.length > 0) {
  //           // Extract unique values for routes from actual data
  //           const routes = [...new Set(allData.map(item => item.ROUTE))]
  //             .filter(Boolean)
  //             .sort((a, b) => {
  //               // Handle both numeric and string routes
  //               if (typeof a === 'number' && typeof b === 'number') {
  //                 return a - b;
  //               }
  //               return String(a).localeCompare(String(b));
  //             });
            
  //           // Update routes if we found more than in metadata
  //           if (routes.length > 0) {
  //             setFilterOptions(prevOptions => ({
  //               ...prevOptions,
  //               routes: routes
  //             }));
  //             console.log(`Updated routes from data: ${routes.length} routes found`);
  //           }
  //         }
  //     }
  //   }
  //   loadData();
  // }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Log date range for debugging
      if (dateRange && dateRange[0] && dateRange[1]) {
        console.log('Loading data with date range:', 
          dateRange[0].format('YYYY-MM-DD'), 'to', 
          dateRange[1].format('YYYY-MM-DD'));
      }
      
      // Prepare filters
      const filters = {
        dateRange,
        route: selectedRoute,
        driver: selectedDriver,
        depot: selectedDepot,
        operatorId: selectedOperatorId,
        contract: selectedContract,
        timePeriod: selectedTimePeriod
      };
      
      // Get data from OTRDataService
      const data = await OTRDataService.getFilteredDataWithCoordinates(filters);
      
      // Debug - check available routes in the data
      const availableRoutes = [...new Set(data.map(item => item.ROUTE))].sort((a, b) => a - b);
      console.log('Available routes in data:', availableRoutes);
      
      if (data.length === 0) {
        // Check if we have suggested dates
        const hasSuggestedDates = await OTRDataService.getDataDateRange();
        if (hasSuggestedDates) {
          const { startDate, endDate } = hasSuggestedDates;
          message.warning(
            <span>
              No data found for the selected filters. 
              <Button 
                type="link" 
                size="small" 
                onClick={() => {
                  setDateRange([dayjs(startDate), dayjs(endDate)]);
                  setSelectedDatePreset('custom');
                }}
              >
                Try {dayjs(startDate).format('MMM D')} to {dayjs(endDate).format('MMM D, YYYY')}
              </Button>
            </span>
          );
        } else {
        message.warning('No data found for the selected filters');
        }
        
        setTripData([]);
        setStopData([]);
        setDriverData([]);
        setFilteredData([]);
        setSummaryData({
          insights: [],
          recommendations: []
        });
        setLoading(false);
        return;
      }
      
      // Process data for trip-level view
      const aggregatedTrips = processTripsData(data);
      setTripData(aggregatedTrips);
      setFilteredData(aggregatedTrips);
      
      // Set stop data for map view
      setStopData(data);
      
      // Process driver data
      const driverAnalysis = await OTRDataService.analyzeDriverPerformance(data);
      setDriverData(driverAnalysis);
      
      // Generate summary insights
      const { insights, recommendations } = generateSummaryInsights(
        aggregatedTrips, 
        data, 
        driverAnalysis
      );
      
      setSummaryData({
        insights,
        recommendations
      });
      
      // Reset view to level 1 when data is reloaded
      if (viewLevel !== VIEW_LEVELS.TRIP_AGGREGATE) {
        setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
        setSelectedTrip(null);
        setSelectedDate(null);
      }
      
      message.success(`Loaded ${data.length} records for analysis`);
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load OTR data");
    } finally {
      setLoading(false);
    }
  };

  // Process the raw data into trip-level aggregations
  const processTripsData = (data) => {
    // Group data by trip
    const tripGroups = {};
    
    data.forEach(record => {
      if (!record.TRIP_ID) return;
      
      const tripId = record.TRIP_ID;
      if (!tripGroups[tripId]) {
        tripGroups[tripId] = {
          tripId: tripId,
          route: record.ROUTE,
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: [],
          dates: new Set(),
          driverId: record.DRIVER_ID,
          depot: record.DEPOT,
          operatorId: record.OPERATOR_ID,
          contract: record.CONTRACT
        };
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        tripGroups[tripId].firstStops.push(record);
      } else if (!record.TSN_FIRST_LAST_IND?.length || record.TSN_FIRST_LAST_IND === 'M') {
        tripGroups[tripId].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        tripGroups[tripId].lastStops.push(record);
      } else {
        tripGroups[tripId].midStops.push(record);
      }
      
      // Add to all stops
      tripGroups[tripId].stops.push(record);
      
      // Track unique dates
      if (record.DATE_ID) {
        tripGroups[tripId].dates.add(record.DATE_ID);
      }
    });
    
    // Calculate stats for each trip and convert to array
    const result = Object.values(tripGroups).map(trip => {
      // Calculate performance metrics
      const firstStopStats = calculateStopStats(trip.firstStops);
      const midStopStats = calculateStopStats(trip.midStops);
      const lastStopStats = calculateStopStats(trip.lastStops);
      const overallStats = calculateStopStats(trip.stops);
      
      return {
        tripId: trip.tripId,
        route: trip.route,
        dateCount: trip.dates.size,
        dates: Array.from(trip.dates),
        stopCount: trip.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats,
        driverId: trip.driverId,
        depot: trip.depot,
        operatorId: trip.operatorId,
        contract: trip.contract,
        recordCount: trip.stops.length
      };
    });
    
    // Debug the first few trip objects
    console.log("Sample trip objects:", result.slice(0, 3));
    
    return result;
  };

  // Helper to get the departure variance from a stop record
  const getDepartureVariance = (stop) => {
    if (Number.isFinite(stop.DEPARTURE_TIME_VARIANCE)) {
      return stop.DEPARTURE_TIME_VARIANCE;
    } else if (Number.isFinite(stop.A_DEP) && Number.isFinite(stop.S_DEP)) {
      return stop.A_DEP - stop.S_DEP;
    } else if (Number.isFinite(stop.ARRIVAL_TIME_VARIANCE)) {
      // Fallback to arrival variance if that's all we have
      return stop.ARRIVAL_TIME_VARIANCE;
    }
    return null;
  };

  // Helper to calculate stop statistics
  const calculateStopStats = (stops) => {
    if (!stops || stops.length === 0) {
      return {
        onTimePercent: 0,
        earlyPercent: 0, 
        latePercent: 0,
        earlyCount: 0,
        onTimeCount: 0,
        lateCount: 0,
        count: 0
      };
    }
    
    // Prioritise departure variance for consistency with Level 3 view
    const stopsWithVariance = stops.filter(s => getDepartureVariance(s) !== null);
    
    if (stopsWithVariance.length === 0) {
      return {
        onTimePercent: 0,
        earlyPercent: 0,
        latePercent: 0,
        earlyCount: 0,
        onTimeCount: 0,
        lateCount: 0,
        count: stops.length
      };
    }
    
    // Update OTR rules: On time is between 2 minutes early and 5 minutes late
    const onTimeCount = stopsWithVariance.filter(s => {
      const variance = getDepartureVariance(s);
      return variance >= OTR_THRESHOLDS.early && variance <= OTR_THRESHOLDS.late;
    }).length;
    
    const earlyCount = stopsWithVariance.filter(s => {
      const variance = getDepartureVariance(s);
      return variance < OTR_THRESHOLDS.early;
    }).length;
    
    const lateCount = stopsWithVariance.filter(s => {
      const variance = getDepartureVariance(s);
      return variance > OTR_THRESHOLDS.late;
    }).length;
    
    return {
      onTimePercent: (onTimeCount / stopsWithVariance.length) * 100,
      earlyPercent: (earlyCount / stopsWithVariance.length) * 100,
      latePercent: (lateCount / stopsWithVariance.length) * 100,
      earlyCount,
      onTimeCount,
      lateCount,
      count: stops.length
    };
  };

  // When user clicks on a trip in Level 1
  const handleTripClick = async (tripId) => {
    // Find the selected trip
    const trip = tripData.find(t => t.tripId === tripId);
    if (!trip) {
      message.error("Trip not found");
      return;
    }

    // Get date-level data for this trip
    const dateData = await getTripDateData(tripId);
    setSelectedTrip(trip);
    setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
    setFilteredData(dateData);
  };

  // Get trip data by date
  const getTripDateData = async (tripId) => {
    // Get raw data from OTRDataService
    const allData = await OTRDataService.retrieveAllData();
    
    // Filter for selected trip
    const tripRecords = allData.filter(r => r.TRIP_ID === tripId);
    
    console.log(`Found ${tripRecords.length} records for trip ${tripId}`);
    
    // Debug: Check the date information in the records
    const uniqueDates = [...new Set(tripRecords.map(r => r.DATE_ID))];
    console.log(`Found ${uniqueDates.length} unique dates for trip ${tripId}:`, uniqueDates);

    // Debug sample records to check DATE_ID field
    if (tripRecords.length > 0) {
      console.log('Sample record DATE_ID:', tripRecords[0].DATE_ID, 'type:', typeof tripRecords[0].DATE_ID);
      console.log('Sample record:', tripRecords[0]);
    }
    
    // Group by date - ensure we're treating DATE_ID consistently as strings
    const dateGroups = {};
    tripRecords.forEach(record => {
      if (!record.DATE_ID) return;
      
      // Convert DATE_ID to string to ensure consistent grouping
      const dateId = String(record.DATE_ID);
      
      if (!dateGroups[dateId]) {
        dateGroups[dateId] = {
          tripId: tripId,
          dateId: dateId,
          route: record.ROUTE,
          date: record.DATE_FORMATTED || dayjs(dateId).format('YYYY-MM-DD'),
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: []
        };
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        dateGroups[dateId].firstStops.push(record);
      } else if (!record.TSN_FIRST_LAST_IND?.length || record.TSN_FIRST_LAST_IND === 'M') {
        dateGroups[dateId].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        dateGroups[dateId].lastStops.push(record);
      }
      
      // Add to all stops
      dateGroups[dateId].stops.push(record);
    });
    
    console.log(`Organised data into ${Object.keys(dateGroups).length} date groups:`, Object.keys(dateGroups));
    
    // Calculate stats for each date and convert to array
    return Object.values(dateGroups).map(dateGroup => {
      const firstStopStats = calculateStopStats(dateGroup.firstStops);
      const midStopStats = calculateStopStats(dateGroup.midStops);
      const lastStopStats = calculateStopStats(dateGroup.lastStops);
      const overallStats = calculateStopStats(dateGroup.stops);
      
      // Prepare chart data for this date
      const chartData = prepareDateChartData(dateGroup.stops);
      
      return {
        tripId: dateGroup.tripId,
        dateId: dateGroup.dateId,
        date: dateGroup.date,
        route: dateGroup.route,
        stopCount: dateGroup.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats,
        chartData // Add chart data
      };
    });
  };
  
  // Helper to prepare chart data for a single date's stops
  const prepareDateChartData = (stops) => {
    // Sort stops by sequence
    const sortedStops = determineStopSequence(stops);
    
    return sortedStops.map((stop, index) => {
      // Prioritise departure variance
      let variance = null;
      
      // First check for departure variance
      if (Number.isFinite(stop.DEPARTURE_TIME_VARIANCE)) {
        variance = stop.DEPARTURE_TIME_VARIANCE;
      } 
      // Then try to calculate from compact departure time fields
      else if (Number.isFinite(stop.A_DEP) && Number.isFinite(stop.S_DEP)) {
        variance = stop.A_DEP - stop.S_DEP;
      }
      // Fall back to arrival variance only if departure variance is not available
      else if (Number.isFinite(stop.ARRIVAL_TIME_VARIANCE)) {
        variance = stop.ARRIVAL_TIME_VARIANCE;
      }
      
      return {
        name: `Stop ${index + 1}`,
        stopId: stop.TSN || '',
        variance: variance,
        sequence: stop.TIME_RECORD_SEQUENCE?.length ? stop.TIME_RECORD_SEQUENCE : (index + 1),
        scheduled: stop.SCHEDULED_DEPARTURE_TIME || stop.SCHEDULED_ARRIVAL_TIME,
        actual: stop.ACTUAL_DEPARTURE_TIME || stop.ACTUAL_ARRIVAL_TIME,
        status: getOnTimeStatus(variance)
      };
    });
  };
  
  // Helper to determine stop sequence for chart data
  const determineStopSequence = (stops) => {
    // First check for TIME_RECORD_SEQUENCE field (from TPT file)
    const hasTimeRecordSequence = stops.some(stop => stop.TIME_RECORD_SEQUENCE?.length);
    
    if (hasTimeRecordSequence) {
      return [...stops].sort((a, b) => {
        // Convert to number in case it's stored as string
        const seqA = Number(a.TIME_RECORD_SEQUENCE || 0);
        const seqB = Number(b.TIME_RECORD_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    // Next try to use any existing sequence data
    const hasSequence = stops.some(stop => stop.SEQUENCE_NUMBER?.length);
    
    if (hasSequence) {
      return [...stops].sort((a, b) => {
        const seqA = Number(a.SEQUENCE_NUMBER || 0);
        const seqB = Number(b.SEQUENCE_NUMBER || 0);
        return seqA - seqB;
      });
    }
    
    // If we have STOP_SEQUENCE field (another common field in transit data)
    const hasStopSequence = stops.some(stop => stop.STOP_SEQUENCE?.length);
    
    if (hasStopSequence) {
      return [...stops].sort((a, b) => {
        const seqA = Number(a.STOP_SEQUENCE || 0);
        const seqB = Number(b.STOP_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    // Infer sequence based on first/mid/last indicators and scheduled times
    let firstStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'F');
    let midStops = stops.filter(stop => !stop.TSN_FIRST_LAST_IND?.length || stop.TSN_FIRST_LAST_IND === 'M');
    let lastStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'L');
    
    // Sort first, mid, and last stops by scheduled time if available
    if (firstStops.length > 0) {
      firstStops = sortStopsByTime(firstStops);
    }
    
    if (midStops.length > 0) {
      midStops = sortStopsByTime(midStops);
    }
    
    if (lastStops.length > 0) {
      lastStops = sortStopsByTime(lastStops);
    }
    
    // Combine and add sequence numbers
    const sortedStops = [...firstStops, ...midStops, ...lastStops].map((stop, index) => ({
      ...stop,
      SEQUENCE_NUMBER: index + 1
    }));
    
    return sortedStops;
  };
  
  // Helper to sort stops by scheduled time
  const sortStopsByTime = (stops) => {
    return [...stops].sort((a, b) => {
      // Try different time fields in order of preference
      const timeA = a.SCHEDULED_ARRIVAL_TIME || a.SCHEDULED_DEPARTURE_TIME || 0;
      const timeB = b.SCHEDULED_ARRIVAL_TIME || b.SCHEDULED_DEPARTURE_TIME || 0;
      return timeA - timeB;
    });
  };

  // When user clicks on a date in Level 2
  const handleDateClick = (tripId, dateId) => {
    setSelectedDate(dateId);
    setViewLevel(VIEW_LEVELS.TRIP_DETAIL);
    
    // Log for debugging
    console.log(`Transitioning to trip detail view for tripId=${tripId}, dateId=${dateId}`);
  };

  // When user clicks on a stop in the Stop Level view
  const handleStopClick = async (tsn) => {
    console.log(`Transitioning to stop detail view for stop TSN=${tsn}`);
    
    // Get all data about this stop
    const allData = await OTRDataService.retrieveAllData();
    const tripsWithStop = allData.filter(record => record.TSN === tsn);
    
    // Group stop data to get statistics
    const stopStats = {};
    
    stopData.forEach(stop => {
      const stopTsn = stop.TSN;
      if (!stopTsn) return;
      
      if (!stopStats[stopTsn]) {
        stopStats[stopTsn] = {
          tsn: stopTsn,
          stopName: stop.STOP_NAME || `Stop ${stopTsn}`
        };
      }
    });
    
    // Get the stop details from stop data if available (more accurate)
    const stopFromStatsArray = Object.values(stopStats).find(s => String(s.tsn) === String(tsn));
    const stopName = stopFromStatsArray ? stopFromStatsArray.stopName : '';
    
    // Create a more complete stop object
    const selectedStopObj = { 
      TSN: tsn,
      STOP_NAME: stopName
    };
    
    setSelectedStop(selectedStopObj);
    setViewLevel(VIEW_LEVELS.STOP_DETAIL);
    
    // Get unique trip IDs
    const uniqueTripIds = [...new Set(tripsWithStop.map(record => record.TRIP_ID))];
    console.log(`Found ${uniqueTripIds.length} trips that include stop ${tsn}`);
    
    // Create a map of trip ID to stop type (First, Mid, Last) for this stop
    const tripStopTypeMap = {};
    tripsWithStop.forEach(record => {
      if (record.TRIP_ID && record.TSN_FIRST_LAST_IND) {
        let typeLabel = '';
        if (record.TSN_FIRST_LAST_IND === 'F') typeLabel = 'First';
        else if (record.TSN_FIRST_LAST_IND === 'M') typeLabel = 'Mid';
        else if (record.TSN_FIRST_LAST_IND === 'L') typeLabel = 'Last';
        
        if (typeLabel) {
          tripStopTypeMap[record.TRIP_ID] = typeLabel;
        }
      }
    });
    
    // Filter trip data to only include trips with this stop and add stop type information
    const stopsFilteredTrips = tripData
      .filter(trip => uniqueTripIds.includes(trip.tripId))
      .map(trip => ({
        ...trip,
        stopType: tripStopTypeMap[trip.tripId] || 'Unknown'
      }));
    
    setFilteredData(stopsFilteredTrips);
    
    // Switch to the trip tab if not already there
    if (activeTab !== 'trip') {
      setActiveTab('trip');
    }
  };

  // Handle going back to previous view level
  const handleBack = async () => {
    if (viewLevel === VIEW_LEVELS.TRIP_BY_DATE) {
      // Go back to Level 1
      setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
      setSelectedTrip(null);
      setFilteredData(tripData);
    } else if (viewLevel === VIEW_LEVELS.TRIP_DETAIL) {
      // Go back to Level 2
      setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
      setSelectedDate(null);
      // Refresh the date-level data
      if (selectedTrip) {
        const dateData = await getTripDateData(selectedTrip.tripId);
        setFilteredData(dateData);
      }
    } else if (viewLevel === VIEW_LEVELS.STOP_DETAIL) {
      // Go back to Stop Level view
      setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
      setSelectedStop(null);
      setFilteredData(tripData);
      // Switch back to stop tab
      setActiveTab('stop');
    }
  };

  // Render OTR performance tag
  const renderOTRTag = (percent) => {
    let color = 'green';
    if (percent < 60) color = 'blue'; // Late - blue
    else if (percent < 80) color = 'red'; // Early - red
    
    return (
      <Tag color={color}>
        {percent.toFixed(1)}%
      </Tag>
    );
  };

  // Columns for Level 1: Trip Aggregate View
  const tripColumns = [
    {
      title: 'Trip ID',
      dataIndex: 'tripId',
      key: 'tripId',
      sorter: (a, b) => Number(a.tripId || 0) - Number(b.tripId || 0),
      render: (tripId) => (
        <Button 
          type="link" 
          style={{ 
            padding: 0, 
            fontSize: '14px', 
            fontWeight: 'bold',
            textDecoration: 'underline'
          }}
          onClick={() => handleTripClick(tripId)}
        >
          {tripId}
        </Button>
      ),
      width: 100,
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      sorter: (a, b) => {
        // Handle both numeric and string routes
        const routeA = a.route;
        const routeB = b.route;
        if (typeof routeA === 'number' && typeof routeB === 'number') {
          return routeA - routeB;
        }
        return String(routeA || '').localeCompare(String(routeB || ''));
      },
      filters: filterOptions.routes.map(route => ({
        text: String(route),
        value: route,
      })),
      onFilter: (value, record) => {
        // Handle both numeric and string values
        if (typeof record.route === 'number' && typeof value === 'string') {
          return record.route === Number(value);
        }
        return String(record.route) === String(value);
      },
      width: 80,
    },
    {
      title: 'Trips',
      dataIndex: 'dateCount',
      key: 'dateCount',
      sorter: (a, b) => a.dateCount - b.dateCount,
      width: 40,
    },
    // {
    //   title: 'Stop Count',
    //   dataIndex: 'stopCount',
    //   key: 'stopCount',
    //   sorter: (a, b) => a.stopCount - b.stopCount,
    //   width: 40,
    // },
    {
      title: 'First OTR %',
      dataIndex: ['firstStopStats', 'onTimePercent'],
      key: 'firstStopOTR',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.firstStopStats?.earlyPercent || 0)}% early\n${Math.round(record.firstStopStats?.latePercent || 0)}% late`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Mid OTR %',
      key: 'midStopOTR',
      dataIndex: ['midStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.midStopStats?.earlyPercent || 0)}% early\n${Math.round(record.midStopStats?.latePercent || 0)}% late`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Last OTR %',
      key: 'lastStopOTR',
      dataIndex: ['lastStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.lastStopStats?.earlyPercent || 0)}% early\n${Math.round(record.lastStopStats?.latePercent || 0)}% late`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Overall OTR %',
      key: 'overallOTR',
      dataIndex: ['overallStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.overallStats?.earlyPercent || 0)}% early\n${Math.round(record.overallStats?.latePercent || 0)}% late`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'AI Insights',
      key: 'insights',
      dataIndex: 'insights',
      render: (_, record) => {
        // Generate AI insights using the ruleset
        const aiInsight = analyzeTripWithRuleset(record, tripData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return <Tag color="default">No insights</Tag>;
        }
        
        // Create a condensed insight indicator based on the type of insight
        let insightType = "Issue";
        let tagColor = "#f50";
        
        // Categorise insights by common types for short display
        if (aiInsight.insight.includes("early departure")) {
          insightType = "Early departures";
          tagColor = "#fa8c16"; // Orange
        } else if (aiInsight.insight.includes("first stop")) {
          insightType = "First stop issues";
          tagColor = "#722ed1"; // Purple
        } else if (aiInsight.insight.includes("lateness")) {
          insightType = "Progressive delays";
          tagColor = "#1890ff"; // Blue
        } else if (aiInsight.insight.includes("Critical")) {
          insightType = "Critical OTR";
          tagColor = "#f5222d"; // Red  
        }
        
        // Add improvement percentage if available
        const improvementText = aiInsight.improvement ? ` +${aiInsight.improvement}%` : '';
        
        // Store insight type and improvement in record for sorting/filtering
        record.insightType = insightType;
        record.insightImprovement = aiInsight.improvement || 0;
        
        return (
          <Popover 
            content={
              <OTRInsightCard 
                title={`Trip ${record.tripId} Insights`}
                insight={aiInsight.insight}
                recommendation={aiInsight.recommendation}
                improvement={aiInsight.improvement}
                category="trip"
              />
            } 
            title={null}
            trigger="click"
          >
            <Tag 
              color={tagColor}
              icon={<BulbOutlined />}
              style={{ cursor: 'pointer' }}
            >
              {insightType}{improvementText}
            </Tag>
          </Popover>
        );
      },
      sorter: (a, b) => {
        const insightA = analyzeTripWithRuleset(a, tripData);
        const insightB = analyzeTripWithRuleset(b, tripData);
        
        const improvementA = insightA?.improvement || 0;
        const improvementB = insightB?.improvement || 0;
        
        return improvementB - improvementA; // Sort by highest improvement first
      },
      filters: [
        { text: 'Early departures', value: 'Early departures' },
        { text: 'First stop issues', value: 'First stop issues' },
        { text: 'Progressive delays', value: 'Progressive delays' },
        { text: 'Critical OTR', value: 'Critical OTR' },
        { text: 'No issues', value: 'No insights' }
      ],
      onFilter: (value, record) => {
        const aiInsight = analyzeTripWithRuleset(record, tripData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return value === 'No insights';
        }
        
        let insightType = "Issue";
        
        // Categorise for filtering
        if (aiInsight.insight.includes("early departure")) {
          insightType = "Early departures";
        } else if (aiInsight.insight.includes("first stop")) {
          insightType = "First stop issues";
        } else if (aiInsight.insight.includes("lateness")) {
          insightType = "Progressive delays";
        } else if (aiInsight.insight.includes("Critical")) {
          insightType = "Critical OTR";
        }
        
        return insightType === value;
      },
      width: 180,
    }
  ];

  // Columns for Level 2: Trip by Date View
  const tripDateColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => String(a.dateId || '').localeCompare(String(b.dateId || '')),
      render: (date, record) => (
        <Button 
          type="link" 
          onClick={() => handleDateClick(record.tripId, record.dateId)}
          style={{ padding: 0 }}
        >
          {date}
        </Button>
      ),
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
    },
    {
      title: 'Stop Count',
      dataIndex: 'stopCount',
      key: 'stopCount',
      sorter: (a, b) => a.stopCount - b.stopCount,
    },
    {
      title: 'First Stop OTR',
      dataIndex: 'firstStopStats',
      key: 'firstStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        
        if (lateCount > earlyCount && lateCount > onTimeCount) {
          return <Tag color="blue">Late</Tag>;
        } else if (earlyCount > onTimeCount && earlyCount > lateCount) {
          return <Tag color="red">Early</Tag>;
        } else {
          return <Tag color="green">On Time</Tag>;
        }
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.firstStopStats) - getOTRValue(b.firstStopStats);
      },
    },
    {
      title: 'Mid Stop OTR',
      dataIndex: 'midStopStats',
      key: 'midStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        
        if (lateCount > earlyCount && lateCount > onTimeCount) {
          return <Tag color="blue">Late</Tag>;
        } else if (earlyCount > onTimeCount && earlyCount > lateCount) {
          return <Tag color="red">Early</Tag>;
        } else {
          return <Tag color="green">On Time</Tag>;
        }
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.midStopStats) - getOTRValue(b.midStopStats);
      },
    },
    {
      title: 'Last Stop OTR',
      dataIndex: 'lastStopStats',
      key: 'lastStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        
        if (lateCount > earlyCount && lateCount > onTimeCount) {
          return <Tag color="blue">Late</Tag>;
        } else if (earlyCount > onTimeCount && earlyCount > lateCount) {
          return <Tag color="red">Early</Tag>;
        } else {
          return <Tag color="green">On Time</Tag>;
        }
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.lastStopStats) - getOTRValue(b.lastStopStats);
      },
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallStats',
      key: 'overallOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        
        if (lateCount > earlyCount && lateCount > onTimeCount) {
          return <Tag color="blue">Late</Tag>;
        } else if (earlyCount > onTimeCount && earlyCount > lateCount) {
          return <Tag color="red">Early</Tag>;
        } else {
          return <Tag color="green">On Time</Tag>;
        }
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.overallStats) - getOTRValue(b.overallStats);
      },
    },
    // {
    //   title: 'Action',
    //   key: 'action',
    //   render: (_, record) => (
    //     <Button 
    //       type="primary" 
    //       size="small" 
    //       onClick={() => handleDateClick(record.tripId, record.dateId)}
    //     >
    //       View Stops
    //     </Button>
    //   ),
    // }
  ];

  // Handle import completion
  const handleImportComplete = async (importedData) => {
    // Process and store imported data
    try {
      const metadata = await OTRDataService.storeData(importedData);
      
      // Update filter options
      setFilterOptions(prevOptions => ({
        ...prevOptions,
        routes: metadata.routes || [],
        drivers: metadata.drivers || [],
        depots: metadata.depots || [],
        operatorIds: metadata.operatorIds || [],
        contracts: metadata.contracts || []
      }));
      
      // Load data with current filters
      loadData();

      // Hide importer
      setShowImporter(false);
      
      message.success(`Successfully stored ${metadata.totalRecords} OTR records for analysis`);
    } catch (error) {
      console.error('Error storing imported data:', error);
      message.error(`Failed to store imported data: ${error.message}`);
    }
  };

  // Render navigation breadcrumb
  const renderBreadcrumb = () => {
    return (
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => {
            setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
            setSelectedTrip(null);
            setSelectedDate(null);
            setSelectedStop(null);
            setFilteredData(tripData);
          }}>
            <HomeOutlined /> Trips
          </a>
        </Breadcrumb.Item>
        
        {viewLevel >= VIEW_LEVELS.TRIP_BY_DATE && selectedTrip && (
          <Breadcrumb.Item>
            <a onClick={() => {
              setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
              setSelectedDate(null);
              getTripDateData(selectedTrip.tripId).then(setFilteredData);
            }}>
              Trip {selectedTrip.tripId}
            </a>
          </Breadcrumb.Item>
        )}
        
        {viewLevel === VIEW_LEVELS.TRIP_DETAIL && selectedDate && (
          <Breadcrumb.Item>
            Date {dayjs(String(selectedDate)).format('YYYY-MM-DD')}
          </Breadcrumb.Item>
        )}

        {viewLevel === VIEW_LEVELS.STOP_DETAIL && selectedStop && (
          <Breadcrumb.Item>
            Stop {selectedStop.TSN} {selectedStop.STOP_NAME && `(${selectedStop.STOP_NAME})`}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>
    );
  };

  // Add new function to render the Performance Overview tab
  const renderPerformanceOverviewTab = () => {

    // Filter the data by selected stop types
    const filteredStopData = filterDataByStopType(stopData);

    // Calculate aggregated stats for the filtered data
    const calculateAggregatedStats = () => {
      if (!filteredStopData || filteredStopData.length === 0) {
        return {
          onTimePercent: 0,
          earlyPercent: 0,
          latePercent: 0,
          total: 0,
          stopsWithVariance: 0,
          stopsWithoutVariance: 0
        };
      }

      let onTimeCount = 0;
      let earlyCount = 0;
      let lateCount = 0;
      let totalCount = 0;
      let stopsWithVariance = 0;
      let stopsWithoutVariance = 0;

      filteredStopData.forEach(record => {
        totalCount++;
        
        // Calculate on-time status
        let variance = null;
        if (Number.isFinite(record.DEPARTURE_TIME_VARIANCE)) {
          variance = record.DEPARTURE_TIME_VARIANCE;
        } else if (Number.isFinite(record.A_DEP) && Number.isFinite(record.S_DEP)) {
          variance = record.A_DEP - record.S_DEP;
        } else if (Number.isFinite(record.ARRIVAL_TIME_VARIANCE)) {
          variance = record.ARRIVAL_TIME_VARIANCE;
        }
        
        if (variance !== null) {
          stopsWithVariance++;
          if (variance >= OTR_THRESHOLDS.early && variance <= OTR_THRESHOLDS.late) {
            onTimeCount++;
          } else if (variance < OTR_THRESHOLDS.early) {
            earlyCount++;
          } else {
            lateCount++;
          }
        } else {
          stopsWithoutVariance++;
          // Stops without variance data are treated as non-compliant
        }
      });

      return {
        onTimePercent: totalCount > 0 ? (onTimeCount / totalCount) * 100 : 0,
        earlyPercent: totalCount > 0 ? (earlyCount / totalCount) * 100 : 0,
        latePercent: totalCount > 0 ? (lateCount / totalCount) * 100 : 0,
        total: totalCount,
        stopsWithVariance: stopsWithVariance,
        stopsWithoutVariance: stopsWithoutVariance
      };
    };

    const aggregatedStats = calculateAggregatedStats();

    // Handle stop type selection change
    const handleStopTypeChange = (types) => {
      // Ensure at least one option is selected
      if (types.length === 0) {
        setSelectedStopTypes(['F']); // Default to "First" if nothing selected
        return;
      }
      
      // Handle "All" option
      if (types.includes('ALL')) {
        // If "All" was just selected, make it the only selection
        if (!selectedStopTypes.includes('ALL')) {
          setSelectedStopTypes(['ALL']);
        } else {
          // If other options were also selected, remove "All"
          const otherTypes = types.filter(type => type !== 'ALL');
          if (otherTypes.length > 0) {
            setSelectedStopTypes(otherTypes);
          } else {
            // Keep "All" if it's the only selection
            setSelectedStopTypes(['ALL']);
          }
        }
      } else {
        setSelectedStopTypes(types);
      }
    };

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <Row gutter={16}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={
                        <Space>
                          <span>On-Time Performance</span>
                          <Tooltip title={`Percentage of buses that arrive between ${Math.abs(OTR_THRESHOLDS.early / 60)} minutes early and ${OTR_THRESHOLDS.late / 60} minutes late across all stops. This is calculated by dividing the number of on-time arrivals by the total number of monitored arrivals.`}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      }
                      value={Math.round(aggregatedStats.onTimePercent)}
                      suffix="%"
                      valueStyle={{ color: '#3f8600' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Card>
                </Col>
                
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={
                        <Space>
                          <span>Early Departures</span>
                          <Tooltip title={`Percentage of buses that depart more than ${Math.abs(OTR_THRESHOLDS.early / 60)} minutes before the scheduled time. Early departures may cause passengers to miss their bus.`}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      }
                      value={Math.round(aggregatedStats.earlyPercent)}
                      suffix="%"
                      valueStyle={{ color: '#fa541c' }}
                      prefix={<FieldTimeOutlined />}
                    />
                  </Card>
                </Col>
                
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={
                        <Space>
                          <span>Late Departures</span>
                          <Tooltip title={`Percentage of buses that depart more than ${OTR_THRESHOLDS.late / 60} minutes after the scheduled time. Late departures often impact the rest of the route and connecting services.`}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      }
                      value={Math.round(aggregatedStats.latePercent)}
                      suffix="%"
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<CloseCircleOutlined />}
                    />
                  </Card>
                </Col>
                
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={
                        <Space>
                          <span>Analysed Trips</span>
                          <Tooltip title="Total number of route variants (unique trip patterns) analysed within the selected time period. Each variant represents a unique route and direction combination.">
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </Space>
                      }
                      value={tripData.length}
                      prefix={<CarOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* OTR Trend Section */}
              <Row gutter={16} style={{ marginTop: '16px' }}>
                <Col span={18}>
                  <Card 
                    title={
                      <Space>
                        <span>Daily OTR Performance</span>
                        <Tooltip title="Shows the daily on-time running performance across the selected date range. Each bar represents a single day.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                  >
                    {(() => {
                      if (!filteredStopData || filteredStopData.length === 0) return <Empty description="No data available" />;
                      
                      // Group data by date to calculate daily OTR
                      const dailyOTR = {};
                      
                      filteredStopData.forEach(record => {
                        const dateId = record.DATE_ID;
                        if (!dateId) return;
                        
                        const dateStr = String(dateId);
                        if (!dailyOTR[dateStr]) {
                          dailyOTR[dateStr] = {
                            date: dateStr,
                            displayDate: record.DATE_FORMATTED || dayjs(dateStr).format('YYYY-MM-DD'),
                            totalStops: 0,
                            onTimeStops: 0,
                            earlyStops: 0,
                            lateStops: 0,
                            // Add tracking for variance type
                            departureVarianceCount: 0,
                            arrivalVarianceCount: 0,
                            noVarianceCount: 0,
                            // Add a field to track stops with valid variance data (for correct percentage calculation)
                            stopsWithVariance: 0
                          };
                        }
                        
                        dailyOTR[dateStr].totalStops++;
                        
                        // Calculate on-time status
                        let variance = null;
                        if (Number.isFinite(record.DEPARTURE_TIME_VARIANCE)) {
                          variance = record.DEPARTURE_TIME_VARIANCE;
                          dailyOTR[dateStr].departureVarianceCount++;
                        } else if (Number.isFinite(record.A_DEP) && Number.isFinite(record.S_DEP)) {
                          variance = record.A_DEP - record.S_DEP;
                          dailyOTR[dateStr].departureVarianceCount++;
                        } else if (Number.isFinite(record.ARRIVAL_TIME_VARIANCE)) {
                          variance = record.ARRIVAL_TIME_VARIANCE;
                          dailyOTR[dateStr].arrivalVarianceCount++;
                        } else {
                          dailyOTR[dateStr].noVarianceCount++;
                        }
                        
                        if (variance !== null) {
                          // Increment the count of stops with valid variance data
                          dailyOTR[dateStr].stopsWithVariance++;
                          
                          if (variance >= OTR_THRESHOLDS.early && variance <= OTR_THRESHOLDS.late) {
                            dailyOTR[dateStr].onTimeStops++;
                          } else if (variance < OTR_THRESHOLDS.early) {
                            dailyOTR[dateStr].earlyStops++;
                          } else {
                            dailyOTR[dateStr].lateStops++;
                          }
                        }
                      });
                      
                      // Convert to array and sort by date
                      const dailyOTRArray = Object.values(dailyOTR)
                        .map(day => ({
                          ...day,
                          onTimePercent: day.totalStops > 0 ? (day.onTimeStops / day.totalStops) * 100 : 0
                        }))
                        .sort((a, b) => a.date.localeCompare(b.date));
                      
                      // Ensure all dates in the selected range are included
                      const allDates = [];
                      if (dateRange && dateRange[0] && dateRange[1]) {
                        // Create a clone to avoid modifying the original date
                        let currentDate = dateRange[0].clone();
                        const endDate = dateRange[1].clone();
                        
                        // Loop through all dates in the range
                        while (currentDate.isSameOrBefore(endDate, 'day')) {
                          const dateStr = currentDate.format('YYYYMMDD');
                          // Only add if not already in dailyOTR
                          if (!dailyOTR[dateStr]) {
                            allDates.push({
                              date: dateStr,
                              displayDate: currentDate.format('YYYY-MM-DD'),
                              totalStops: 0,
                              onTimeStops: 0,
                              earlyStops: 0,
                              lateStops: 0,
                              departureVarianceCount: 0,
                              arrivalVarianceCount: 0,
                              noVarianceCount: 0,
                              stopsWithVariance: 0,
                              onTimePercent: 0
                            });
                          }
                          // Move to next day
                          currentDate = currentDate.add(1, 'day');
                        }
                      }
                      
                      // Combine existing data with placeholder data for missing dates
                      const completeOTRArray = [...dailyOTRArray, ...allDates]
                        .sort((a, b) => {
                          // Sort by actual date to ensure correct order
                          const dateA = dayjs(a.date);
                          const dateB = dayjs(b.date);
                          return dateA.valueOf() - dateB.valueOf();
                        });
                      
                      // Add trend analysis by comparing first and second half
                      const midpoint = Math.floor(completeOTRArray.length / 2);
                      let firstHalfOTR = 0;
                      let secondHalfOTR = 0;
                      
                      if (completeOTRArray.length > 0) {
                        // Only include dates with data when calculating trends
                        const datesWithData = completeOTRArray.filter(day => day.totalStops > 0);
                        const half = Math.floor(datesWithData.length / 2);
                        
                        if (datesWithData.length > 0) {
                          const firstHalf = datesWithData.slice(0, half);
                          const secondHalf = datesWithData.slice(half);
                          
                          firstHalfOTR = firstHalf.length > 0 ? 
                            firstHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / firstHalf.length : 0;
                          secondHalfOTR = secondHalf.length > 0 ? 
                            secondHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / secondHalf.length : 0;
                        }
                      }
                      
                      const trendPercentage = firstHalfOTR > 0 
                        ? ((secondHalfOTR - firstHalfOTR) / firstHalfOTR) * 100 
                        : 0;
                      
                      // Prepare chart data
                      const chartData = completeOTRArray.map(day => {
                        // Format date in a consistent way for display (DD/MM)
                        let displayDate;
                        try {
                          // Try to parse the date
                          const dateObj = dayjs(day.date);
                          if (dateObj.isValid()) {
                            displayDate = dateObj.format('DD/MM');
                          } else {
                            displayDate = day.displayDate;
                          }
                        } catch (e) {
                          displayDate = day.displayDate;
                        }
                        
                        return {
                          date: displayDate,
                          onTimeValue: Math.round(day.onTimePercent),
                          hasData: day.totalStops > 0,
                          // Add observation stats for tooltip
                          totalObservations: day.totalStops,
                          departureCount: day.departureVarianceCount,
                          arrivalCount: day.arrivalVarianceCount,
                          noVarianceCount: day.noVarianceCount,
                          // Original date for reference
                          originalDate: day.date
                        };
                      });
                      
                      return (
                        <div>
                          {/* Bar Chart */}
                          <div style={{ height: '350px', width: '100%', marginLeft: '-15px' }}>
                            {chartData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%" minWidth={600}>
                                <BarChart
                                  data={chartData}
                                  margin={{ top: 25, right: 20, left: 0, bottom: 25 }}
                                  barSize={45}
                                  barGap={0}
                                  maxBarSize={120}
                                >
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                  <XAxis 
                                    dataKey="date" 
                                    angle={0} 
                                    textAnchor="middle"
                                    height={40}
                                    tickMargin={5}
                                    axisLine={false}
                                    tick={{ fontSize: 12, fill: '#666' }}
                                    interval="preserveStartEnd"
                                    tickLine={false}
                                  />
                                  <YAxis 
                                    domain={[0, 100]}
                                    axisLine={{ stroke: '#e5e5e5' }}
                                    tick={{ fontSize: 11, fill: '#666' }}
                                    tickLine={false}
                                    width={20}
                                    padding={{ left: 0 }}
                                  />
                                  <RechartsTooltip 
                                    formatter={(value, name) => {
                                      // Determine status and color based on value
                                      let status = 'Poor';
                                      let color = '#f5222d'; // red
                                      
                                      if (value >= 95) {
                                        status = 'Excellent';
                                        color = '#52c41a'; // green
                                      } else if (value >= 75) {
                                        status = 'Acceptable';
                                        color = '#faad14'; // amber
                                      }
                                      
                                      return [
                                        <span style={{ color }}>{`${value}% (${status})`}</span>, 
                                        'On Time'
                                      ];
                                    }}
                                    labelFormatter={(label, payload) => {
                                      if (payload && payload.length > 0) {
                                        const entry = payload[0].payload;
                                        return (
                                          <span style={{ display: 'block' }}>
                                            <span style={{ display: 'block' }}><strong>Date: {label}</strong></span>
                                            <span style={{ display: 'block', marginTop: '8px', fontSize: '11px' }}>
                                              <span style={{ display: 'block' }}>Total Observations: {entry.totalObservations}</span>
                                              <span style={{ display: 'block' }}>Departure Time: {entry.departureCount} records ({Math.round((entry.departureCount/entry.totalObservations)*100) || 0}%)</span>
                                              <span style={{ display: 'block' }}>Arrival Time: {entry.arrivalCount} records ({Math.round((entry.arrivalCount/entry.totalObservations)*100) || 0}%)</span>
                                              <span style={{ display: 'block' }}>No Variance: {entry.noVarianceCount} records ({Math.round((entry.noVarianceCount/entry.totalObservations)*100) || 0}%)</span>
                                            </span>
                                          </span>
                                        );
                                      }
                                      return `Date: ${label}`;
                                    }}
                                    contentStyle={{ 
                                      borderRadius: '4px', 
                                      border: 'none', 
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)', 
                                      padding: '8px 12px',
                                      fontSize: '12px',
                                      minWidth: '200px'
                                    }}
                                  />
                                  <ReferenceLine 
                                    y={95} 
                                    stroke="#52c41a" 
                                    strokeDasharray="3 3"
                                    label={{ 
                                      value: '95', 
                                      position: 'right',
                                      fill: '#52c41a',
                                      fontSize: 12
                                    }}
                                  />
                                  <ReferenceLine 
                                    y={75} 
                                    stroke="#faad14" 
                                    strokeDasharray="3 3"
                                    label={{ 
                                      value: '75', 
                                      position: 'right',
                                      fill: '#faad14',
                                      fontSize: 12
                                    }}
                                  />
                                  <ReferenceLine 
                                    y={50} 
                                    stroke="#f5222d" 
                                    strokeDasharray="3 3"
                                    label={{ 
                                      value: '50', 
                                      position: 'right',
                                      fill: '#f5222d',
                                      fontSize: 12
                                    }}
                                  />
                                  <Bar 
                                    dataKey="onTimeValue" 
                                    name="On Time" 
                                    fill="#52c41a"
                                    radius={[4, 4, 0, 0]}
                                    shape={(props) => {
                                      // Skip rendering bar if no data (avoid empty bars)
                                      if (!props.payload.hasData) {
                                        return null;
                                      }
                                      
                                      // Determine color based on value
                                      let fill = '#f5222d'; // red (default for <75%)
                                      
                                      if (props.payload.onTimeValue >= 95) {
                                        fill = '#52c41a'; // green
                                      } else if (props.payload.onTimeValue >= 75) {
                                        fill = '#faad14'; // amber
                                      }
                                      
                                      // Return the custom colored bar
                                      return (
                                        <g>
                                          <rect
                                            x={props.x}
                                            y={props.y}
                                            width={props.width}
                                            height={props.height}
                                            fill={fill}
                                            radius={[4, 4, 0, 0]}
                                            rx={4}
                                          />
                                          <text
                                            x={props.x + props.width / 2}
                                            y={props.y - 10}
                                            textAnchor="middle"
                                            fill={fill}
                                            fontSize={11}
                                            fontWeight="bold"
                                            fontFamily="'Helvetica Neue', Arial, sans-serif"
                                          >
                                            {props.payload.onTimeValue}%
                                          </text>
                                        </g>
                                      );
                                    }}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <Empty description="No data available" />
                            )}
                          </div>
                        </div>
                      );
                    })()}
            </Card>
          </Col>

                <Col span={6}>
                  <Card
                    title={
                      <Space>
                        <span>Performance Trend</span>
                        <Tooltip title="Shows the trend in on-time performance between the first and second half of the selected period. A positive value indicates improvement.">
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                    }
                    style={{ height: '100%' }}
                  >
                    {(() => {
                      if (!filteredStopData || filteredStopData.length === 0) return <Empty description="No data available" />;
                      
                      // Group data by date to calculate daily OTR
                      const dailyOTR = {};
                      
                      filteredStopData.forEach(record => {
                        const dateId = record.DATE_ID;
                        if (!dateId) return;
                        
                        const dateStr = String(dateId);
                        if (!dailyOTR[dateStr]) {
                          dailyOTR[dateStr] = {
                            date: dateStr,
                            displayDate: record.DATE_FORMATTED || dayjs(dateStr).format('YYYY-MM-DD'),
                            totalStops: 0,
                            onTimeStops: 0,
                            stopsWithVariance: 0
                          };
                        }
                        
                        dailyOTR[dateStr].totalStops++;
                        
                        // Calculate on-time status
                        let variance = null;
                        if (Number.isFinite(record.DEPARTURE_TIME_VARIANCE)) {
                          variance = record.DEPARTURE_TIME_VARIANCE;
                        } else if (Number.isFinite(record.A_DEP) && Number.isFinite(record.S_DEP)) {
                          variance = record.A_DEP - record.S_DEP; 
                        } else if (Number.isFinite(record.ARRIVAL_TIME_VARIANCE)) {
                          variance = record.ARRIVAL_TIME_VARIANCE;
                        }
                        
                        if (variance !== null) {
                          dailyOTR[dateStr].stopsWithVariance++;
                          if (variance >= OTR_THRESHOLDS.early && variance <= OTR_THRESHOLDS.late) {
                            dailyOTR[dateStr].onTimeStops++;
                          }
                        }
                      });
                      
                      // Convert to array and sort by date
                      const dailyOTRArray = Object.values(dailyOTR)
                        .map(day => ({
                          ...day,
                          onTimePercent: day.totalStops > 0 ? (day.onTimeStops / day.totalStops) * 100 : 0
                        }))
                        .sort((a, b) => a.date.localeCompare(b.date));
                      
                      // Add trend analysis by comparing first and second half
                      const midpoint = Math.floor(dailyOTRArray.length / 2);
                      let firstHalfOTR = 0;
                      let secondHalfOTR = 0;
                      
                      if (dailyOTRArray.length > 0) {
                        const firstHalf = dailyOTRArray.slice(0, midpoint);
                        const secondHalf = dailyOTRArray.slice(midpoint);
                        
                        firstHalfOTR = firstHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / firstHalf.length;
                        secondHalfOTR = secondHalf.reduce((sum, day) => sum + day.onTimePercent, 0) / secondHalf.length;
                      }
                      
                      const trendPercentage = firstHalfOTR > 0 
                        ? ((secondHalfOTR - firstHalfOTR) / firstHalfOTR) * 100 
                        : 0;
                      
                      const trendText = trendPercentage >= 0 
                        ? `+${trendPercentage.toFixed(1)}%` 
                        : `${trendPercentage.toFixed(1)}%`;
                      
                      const isPositive = trendPercentage >= 0;
                      
                      // Add labels to indicate which stop types are being shown
                      const stopTypeLabels = {
                        'ALL': 'All Stops',
                        'F': 'First Stops',
                        'M': 'Mid Stops',
                        'L': 'Last Stops'
                      };
                      
                      const selectedTypeLabels = selectedStopTypes.map(type => stopTypeLabels[type] || type).join(', ');
                      
                      return (
                        <div style={{ textAlign: 'center', padding: '30px 0' }}>
                          <Statistic
                            value={trendText}
                            valueStyle={{ 
                              color: isPositive ? '#3f8600' : '#cf1322',
                              fontSize: '36px'
                            }}
                            prefix={isPositive ? <ArrowLeftOutlined rotate={45} /> : <ArrowLeftOutlined rotate={-45} />}
                            suffix={
                              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                                from previous period
                              </div>
                            }
                          />
                          <div style={{ marginTop: '16px' }}>
                            <Text type="secondary">
                              First half: {firstHalfOTR.toFixed(1)}%
                            </Text>
                            <br />
                            <Text type="secondary">
                              Second half: {secondHalfOTR.toFixed(1)}%
                            </Text>
                            <br />
                            <Tag color="blue" style={{ marginTop: '8px' }}>
                              {selectedTypeLabels}
                            </Tag>
                          </div>
                        </div>
                      );
                    })()}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Update the renderSummaryDashboard function to remove the performance overview
  const renderSummaryDashboard = () => {
    // Format date range for display
    const dateRangeText = dateRange && dateRange[0] && dateRange[1] 
      ? `${dateRange[0].format('DD MMM YYYY')} - ${dateRange[1].format('DD MMM YYYY')}` 
      : 'All Time';
      
    // Check if we have data
    if (!summaryData.insights.length && !summaryData.recommendations.length) {
      return (
        <Empty 
          description="No summary data available" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Filter the data by selected stop types, just like in Performance Overview tab
    const filteredStopData = filterDataByStopType(stopData);
    
    // Add debug logging
    console.log(`[renderSummaryDashboard] Passing ${filteredStopData.length} records to OTRPerformanceRadialChart`);
    console.log(`[renderSummaryDashboard] Selected stop types: ${selectedStopTypes.join(',')}`);
    
    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <OTRPerformanceRadialChart 
              data={filteredStopData} 
              title={
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space>
                      <span>Daily Performance</span>
                      <Tooltip title="Visual representation of on-time performance by route. The chart shows the percentage of on-time, early, and late services for each route in the selected time period.">
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                      <Tag color="blue">{dateRangeText}</Tag>
                    </Space>
                  </Col>
                </Row>
              }
            />
          </Col>
          
          <Col span={24}>
            <Card title="Top OTR Issues" bordered={false}>
              <List
                itemLayout="horizontal"
                dataSource={summaryData.insights}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<InfoCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                      title={item.title}
                      description={item.description}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card title="Recommendations" bordered={false}>
              <List
                itemLayout="horizontal"
                dataSource={summaryData.recommendations}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />}
                      title={item.title}
                      description={item.description}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Add the handler function for date preset changes
  const handleDatePresetChange = (preset) => {
    setSelectedDatePreset(preset);
    const today = dayjs();
    let startDate, endDate;
    switch (preset) {
      case 'last30':
        startDate = today.subtract(30, 'day');
        endDate = today.endOf('day');
        break;
      case 'last60':
        startDate = today.subtract(60, 'day');
        endDate = today.endOf('day');
        break;
      case 'thisQuarter':
        startDate = today.startOf('quarter');
        endDate = today.endOf('quarter').endOf('day');
        break;
      case 'lastQuarter':
        startDate = today.subtract(1, 'quarter').startOf('quarter');
        endDate = today.subtract(1, 'quarter').endOf('quarter').endOf('day');
        break;
      case 'custom':
        return;
      default:
        startDate = today.subtract(30, 'day');
        endDate = today.endOf('day');
    }
    setDateRange([startDate, endDate]);
  };

  // Helper function to determine on-time status
  const getOnTimeStatus = (variance) => {
    if (variance === undefined || variance === null) return 'UNKNOWN';
    if (variance >= OTR_THRESHOLDS.early && variance <= OTR_THRESHOLDS.late) return 'ON_TIME';
    if (variance < OTR_THRESHOLDS.early) return 'EARLY';
    return 'LATE';
  };

  // Add the renderDailyOverviewTab function
  const renderDailyOverviewTab = () => {
    if (!stopData || stopData.length === 0) {
      return (
        <Empty 
          description="No daily data available" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      );
    }
    
    return (
      <OTRDailyOverviewTab 
        data={stopData}
        selectedStopTypes={selectedStopTypes}
        thresholds={OTR_THRESHOLDS}
        onStopTypeChange={setSelectedStopTypes}
      />
    );
  };

  // Render the current view based on active tab and level
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            <Text>Loading OTR data...</Text>
          </div>
        </div>
      );
    }

    if (showImporter) {
      return <OTRImporter onImportComplete={handleImportComplete} />;
    }

    if (tripData.length === 0) {
      return (
        <Empty 
          description={
            <span>
              No OTR data available. Please import data first.
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button 
            type="primary" 
            icon={<ImportOutlined />}
            onClick={() => setShowImporter(true)}
          >
            Import TPT Data
          </Button>
        </Empty>
      );
    }

    // Performance Overview Tab
    if (activeTab === 'overview') {
      return renderPerformanceOverviewTab();
    }
    
    // Daily Overview Tab
    if (activeTab === 'dailyOverview') {
      return renderDailyOverviewTab();
    }

    // Trip tab and levels
    if (activeTab === 'trip') {
      // Level 3: Trip Detail View (stop-by-stop)
      if (viewLevel === VIEW_LEVELS.TRIP_DETAIL && selectedTrip && selectedDate) {
        const tripId = selectedTrip.tripId;
        return (
          <OTRTripDetailView 
            tripId={tripId}
            dateId={selectedDate}
            onClose={handleBack}
          />
        );
      }

      // Level 4: Stop Detail View (trips for a specific stop)
      if (viewLevel === VIEW_LEVELS.STOP_DETAIL && selectedStop) {
        // Create columns specifically for the stop detail view
        const stopDetailColumns = [
          ...tripColumns.slice(0, 2), // Take Trip ID and Route columns
          {
            title: 'Stop Type in Trip',
            dataIndex: 'stopType',
            key: 'stopType',
            render: stopType => {
              let color = 'default';
              if (stopType === 'First') color = 'green';
              if (stopType === 'Mid') color = 'blue';
              if (stopType === 'Last') color = 'orange';
              
              return <Tag color={color}>{stopType}</Tag>;
            },
            filters: [
              { text: 'First', value: 'First' },
              { text: 'Mid', value: 'Mid' },
              { text: 'Last', value: 'Last' },
              { text: 'Unknown', value: 'Unknown' }
            ],
            onFilter: (value, record) => record.stopType === value,
          },
          ...tripColumns.slice(2) // Add the remaining columns
        ];
        
        return (
          <>
            <Card
              title={
                <Space>
                  <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                  >
                    Back to Stop Level
                  </Button>
                  {`Trips for Stop ${selectedStop.TSN} ${selectedStop.STOP_NAME ? `(${selectedStop.STOP_NAME})` : ''}`}
                </Space>
              }
            >
              <Table
                columns={stopDetailColumns}
                dataSource={filteredData}
                rowKey="tripId"
                pagination={{ pageSize: 20 }}
              />
            </Card>
          </>
        );
      }

      // Level 1 or 2: Table Views
      return (
        <>
          {/* Add Multi-Trip Chart for Level 2 */}
          {viewLevel === VIEW_LEVELS.TRIP_BY_DATE && filteredData.length > 0 && selectedTrip && (
            <div style={{ marginBottom: '20px' }}>
              <Card title="Trip Variance Comparison Across Dates">
                <OTRStatisticalVarianceChart 
                  selectedTrip={selectedTrip}
                  title="Trip Variance by Stop"
                />
              </Card>
            </div>
          )}
          
          <Card
            title={
              <Space>
                {viewLevel > VIEW_LEVELS.TRIP_AGGREGATE && (
                  <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}
                {viewLevel === VIEW_LEVELS.TRIP_AGGREGATE && 'Trip Performance Summary'}
                {viewLevel === VIEW_LEVELS.TRIP_BY_DATE && selectedTrip && `Trip ${selectedTrip.tripId} Performance by Date`}
              </Space>
            }
          >
            <Table
              columns={viewLevel === VIEW_LEVELS.TRIP_AGGREGATE ? tripColumns : tripDateColumns}
              dataSource={filteredData}
              rowKey={viewLevel === VIEW_LEVELS.TRIP_AGGREGATE ? 'tripId' : record => `${record.tripId}-${record.dateId}`}
              pagination={{ pageSize: 20 }}
            />
          </Card>
        </>
      );
    }
    
    // Stop Map View
    if (activeTab === 'stop') {
      return renderStopMapView();
    }
    
    // Driver Performance View
    if (activeTab === 'driver') {
      return renderDriverPerformanceView();
    }

    // Default fallback
    return (
      <Empty 
        description="Select a tab to view data" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  };

  // Add the missing renderStopMapView function (after renderSummaryDashboard)
  const renderStopMapView = () => {
    if (stopData.length === 0) {
      return (
        <Empty 
          description="No stop data available" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      );
    }

    // Group and aggregate data by stop TSN
    const stopStats = {};
    
    stopData.forEach(stop => {
      const tsn = stop.TSN;
      if (!tsn) return; // Skip stops without TSN
      
      if (!stopStats[tsn]) {
        stopStats[tsn] = {
          tsn: tsn,
          stopName: stop.STOP_NAME || `Stop ${tsn}`,
          latitude: stop.LATITUDE,
          longitude: stop.LONGITUDE,
          departures: 0,
          earlyCount: 0,
          onTimeCount: 0,
          lateCount: 0,
          unknownCount: 0,
          totalCount: 0,
          // Track role flags
          hasFirstStopRole: false,
          hasMidStopRole: false,
          hasLastStopRole: false,
          stopTypes: [],
          // Track stats by stop type (First/Mid/Last)
          firstStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          midStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          lastStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          routes: new Set(),
          averageVariance: 0,
          totalVariance: 0,
          varianceCount: 0,
          variance_values: [],
          consistency: 0,
          routeConsistencyStats: {}
        };
      }
      
      // Add to trackers
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        stopStats[tsn].hasFirstStopRole = true;
      } 
      if (stop.TSN_FIRST_LAST_IND === 'M') {
        stopStats[tsn].hasMidStopRole = true;
      } 
      if (stop.TSN_FIRST_LAST_IND === 'L') {
        stopStats[tsn].hasLastStopRole = true;
      }
      
      // Increment counters
      stopStats[tsn].departures++;
      stopStats[tsn].totalCount++;
      
      // Add route info
      if (stop.ROUTE) {
        stopStats[tsn].routes.add(stop.ROUTE);
        
        // Track performance by route for consistency analysis
        if (!stopStats[tsn].routeConsistencyStats[stop.ROUTE]) {
          stopStats[tsn].routeConsistencyStats[stop.ROUTE] = {
            totalVariance: 0,
            varianceCount: 0,
            varianceValues: [],
            earlyCount: 0,
            onTimeCount: 0,
            lateCount: 0
          };
        }
      }
      
      // Calculate on-time status
      let variance = null;
      if (Number.isFinite(stop.DEPARTURE_TIME_VARIANCE)) {
        variance = stop.DEPARTURE_TIME_VARIANCE;
      } else if (Number.isFinite(stop.A_DEP) && Number.isFinite(stop.S_DEP)) {
        variance = stop.A_DEP - stop.S_DEP;
      } else if (Number.isFinite(stop.ARRIVAL_TIME_VARIANCE)) {
        variance = stop.ARRIVAL_TIME_VARIANCE;
      }
      
      if (variance !== null) {
        // Add to total variance for average calculation
        stopStats[tsn].totalVariance += variance;
        stopStats[tsn].varianceCount++;
        stopStats[tsn].variance_values.push(variance);
        
        // Add to route-specific variance tracking
        if (stop.ROUTE && stopStats[tsn].routeConsistencyStats[stop.ROUTE]) {
          stopStats[tsn].routeConsistencyStats[stop.ROUTE].totalVariance += variance;
          stopStats[tsn].routeConsistencyStats[stop.ROUTE].varianceCount++;
          stopStats[tsn].routeConsistencyStats[stop.ROUTE].varianceValues.push(variance);
        }
        
        // Categorize based on OTR rule
        if (variance < OTR_THRESHOLDS.early) {
          stopStats[tsn].earlyCount++;
          if (stop.ROUTE) stopStats[tsn].routeConsistencyStats[stop.ROUTE].earlyCount++;
          
          // Also track by stop type
          if (stop.TSN_FIRST_LAST_IND === 'F') {
            stopStats[tsn].firstStopStats.earlyCount++;
            stopStats[tsn].firstStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'M') {
            stopStats[tsn].midStopStats.earlyCount++;
            stopStats[tsn].midStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'L') {
            stopStats[tsn].lastStopStats.earlyCount++;
            stopStats[tsn].lastStopStats.total++;
          }
        } else if (variance > OTR_THRESHOLDS.late) {
          stopStats[tsn].lateCount++;
          if (stop.ROUTE) stopStats[tsn].routeConsistencyStats[stop.ROUTE].lateCount++;
          
          // Also track by stop type
          if (stop.TSN_FIRST_LAST_IND === 'F') {
            stopStats[tsn].firstStopStats.lateCount++;
            stopStats[tsn].firstStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'M') {
            stopStats[tsn].midStopStats.lateCount++;
            stopStats[tsn].midStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'L') {
            stopStats[tsn].lastStopStats.lateCount++;
            stopStats[tsn].lastStopStats.total++;
          }
        } else {
          stopStats[tsn].onTimeCount++;
          if (stop.ROUTE) stopStats[tsn].routeConsistencyStats[stop.ROUTE].onTimeCount++;
          
          // Also track by stop type
          if (stop.TSN_FIRST_LAST_IND === 'F') {
            stopStats[tsn].firstStopStats.onTimeCount++;
            stopStats[tsn].firstStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'M') {
            stopStats[tsn].midStopStats.onTimeCount++;
            stopStats[tsn].midStopStats.total++;
          }
          if (stop.TSN_FIRST_LAST_IND === 'L') {
            stopStats[tsn].lastStopStats.onTimeCount++;
            stopStats[tsn].lastStopStats.total++;
          }
        }
      } else {
        stopStats[tsn].unknownCount++;
      }
    });
    
    // Calculate percentages and statistics
    Object.values(stopStats).forEach(stop => {
      const validRecords = stop.earlyCount + stop.onTimeCount + stop.lateCount;
      stop.earlyPercent = validRecords > 0 ? (stop.earlyCount / validRecords) * 100 : 0;
      stop.onTimePercent = validRecords > 0 ? (stop.onTimeCount / validRecords) * 100 : 0;
      stop.latePercent = validRecords > 0 ? (stop.lateCount / validRecords) * 100 : 0;
      
      // Add otrPercent which is needed by the AI ruleset (same as onTimePercent)
      stop.otrPercent = stop.onTimePercent;
      
      // Calculate percentages for stop types
      ['firstStopStats', 'midStopStats', 'lastStopStats'].forEach(statType => {
        if (stop[statType].total > 0) {
          stop[statType].onTimePercent = (stop[statType].onTimeCount / stop[statType].total) * 100;
          stop[statType].earlyPercent = (stop[statType].earlyCount / stop[statType].total) * 100;
          stop[statType].latePercent = (stop[statType].lateCount / stop[statType].total) * 100;
        } else {
          stop[statType].onTimePercent = 0;
          stop[statType].earlyPercent = 0;
          stop[statType].latePercent = 0;
        }
      });
      
      stop.averageVariance = stop.varianceCount > 0 ? stop.totalVariance / stop.varianceCount : 0;
      
      // Fix routes data structure - convert from string to Set if needed
      if (typeof stop.routes === 'string') {
        stop.routeCount = stop.routes.split(',').filter(r => r.trim().length > 0).length;
        // Convert back to Set for route inconsistency rule to work correctly
        if (stop.routeCount > 0) {
          const routesArray = stop.routes.split(',').map(r => r.trim()).filter(Boolean);
          stop.routes = new Set(routesArray);
        } else {
          stop.routes = new Set();
        }
      } else {
        stop.routeCount = stop.routes.size;
      }
      
      // Update route consistency stats with necessary fields for the AI ruleset to work
      Object.keys(stop.routeConsistencyStats || {}).forEach(route => {
        const stats = stop.routeConsistencyStats[route];
        if (stats) {
          // Make sure varianceCount field exists
          if (stats.varianceCount === undefined) {
            stats.varianceCount = (stats.earlyCount || 0) + (stats.onTimeCount || 0) + (stats.lateCount || 0);
          }
        }
      });
      
      // Build stop types array 
      stop.stopTypes = [];
      if (stop.hasFirstStopRole) stop.stopTypes.push('First');
      if (stop.hasMidStopRole) stop.stopTypes.push('Mid');
      if (stop.hasLastStopRole) stop.stopTypes.push('Last');
    });
    
    // Store string version of routes for display after analysis is done
    const stopStatsArray = Object.values(stopStats).map(stop => {
      // Deep clone to avoid reference issues
      const stopCopy = { ...stop };
      // Save routes as string for display
      stopCopy.routesString = Array.from(stop.routes).join(', ');
      return stopCopy;
    });

    // Get stops with potential positioning issues
    const stopsWithIssues = stopStatsArray
      .filter(stop => stop.potentialIssues && stop.potentialIssues.length > 0)
      .sort((a, b) => b.positioningIssueScore - a.positioningIssueScore);
    
    // Filter stops based on search text
    const filteredStopStats = stopSearchText 
      ? stopStatsArray.filter(stop => 
          String(stop.tsn).toLowerCase().includes(stopSearchText.toLowerCase()) || 
          (stop.stopName && stop.stopName.toLowerCase().includes(stopSearchText.toLowerCase()))
        )
      : stopStatsArray;

    // Define columns for the stops table
    const stopColumns = [
      {
        title: 'Stop ID',
        dataIndex: 'tsn',
        key: 'tsn',
        sorter: (a, b) => String(a.tsn).localeCompare(String(b.tsn)),
        render: tsn => (
          <a onClick={() => handleStopClick(tsn)}>{tsn}</a>
        ),
        width: 80,
      },
      {
        title: 'Stop Name',
        dataIndex: 'stopName',
        key: 'stopName',
        sorter: (a, b) => String(a.stopName).localeCompare(String(b.stopName)),
        render: (stopName, record) => (
          <a onClick={() => handleStopClick(record.tsn)}>{stopName}</a>
        ),
        width: 100,
      },
      {
        title: 'Stop Type',
        key: 'stopType',
        render: (_, record) => {
          const stopTypes = record.stopTypes || [];
          return (
            <div>
              {stopTypes.map(type => {
                let color = 'default';
                if (type === 'First') color = 'green';
                if (type === 'Mid') color = 'blue';
                if (type === 'Last') color = 'orange';
                
                return <Tag color={color} key={type}>{type}</Tag>;
              })}
            </div>
          );
        },
        filters: [
          { text: 'First', value: 'First' },
          { text: 'Mid', value: 'Mid' },
          { text: 'Last', value: 'Last' }
        ],
        onFilter: (value, record) => (record.stopTypes || []).includes(value),
        width: 80,
      },
      {
        title: 'Overall OTR %',
        dataIndex: 'onTimePercent',
        key: 'overallOTR',
        render: (val, record) => {
          const value = val || 0;
          let color = 'green';
          if (value < 70) color = 'blue';
          else if (value < 90) color = 'red';
          
          return (
            <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.earlyPercent || 0)}% early\n${Math.round(record.latePercent || 0)}% late`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => a.onTimePercent - b.onTimePercent,
        width: 100,
      },
      {
        title: 'AI Insights',
        key: 'insights',
        dataIndex: 'insights',
        render: (_, record) => {
          // Generate AI insights using the ruleset
          const aiInsight = analyzeStopWithRuleset(record, stopData);
          
          if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
            return <Tag color="default">No insights</Tag>;
          }
          
          // Create a condensed insight indicator based on the type of insight
          let insightType = "Issue";
          let tagColor = "#f50";
          
          // Categorize insights by common types for short display
          if (aiInsight.insight.includes("chronic lateness")) {
            insightType = "Chronic delays";
            tagColor = "#1890ff"; // Blue
          } else if (aiInsight.insight.includes("dwell time")) {
            insightType = "Dwell time";
            tagColor = "#fa8c16"; // Orange
          } else if (aiInsight.insight.includes("inconsistent")) {
            insightType = "Route inconsistency";
            tagColor = "#722ed1"; // Purple
          }
          
          // Add improvement percentage if available
          const improvementText = aiInsight.improvement ? ` +${aiInsight.improvement}%` : '';
          
          // Store insight type and improvement in record for sorting/filtering
          record.insightType = insightType;
          record.insightImprovement = aiInsight.improvement || 0;
          
          return (
            <Popover 
              content={
                <OTRInsightCard 
                  title={`Stop ${record.tsn} Insights`}
                  insight={aiInsight.insight}
                  recommendation={aiInsight.recommendation}
                  improvement={aiInsight.improvement}
                  category="stop"
                />
              } 
              title={null}
              trigger="click"
            >
              <Tag 
                color={tagColor}
                icon={<BulbOutlined />}
                style={{ cursor: 'pointer' }}
              >
                {insightType}{improvementText}
              </Tag>
            </Popover>
          );
        },
        sorter: (a, b) => {
          const insightA = analyzeStopWithRuleset(a, stopData);
          const insightB = analyzeStopWithRuleset(b, stopData);
          
          const improvementA = insightA?.improvement || 0;
          const improvementB = insightB?.improvement || 0;
          
          return improvementB - improvementA; // Sort by highest improvement first
        },
        filters: [
          { text: 'Chronic delays', value: 'Chronic delays' },
          { text: 'Dwell time', value: 'Dwell time' },
          { text: 'Route inconsistency', value: 'Route inconsistency' },
          { text: 'No issues', value: 'No insights' }
        ],
        onFilter: (value, record) => {
          const aiInsight = analyzeStopWithRuleset(record, stopData);
          
          if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
            return value === 'No insights';
          }
          
          let insightType = "Issue";
          
          // Categorize for filtering
          if (aiInsight.insight.includes("chronic lateness")) {
            insightType = "Chronic delays";
          } else if (aiInsight.insight.includes("dwell time")) {
            insightType = "Dwell time";
          } else if (aiInsight.insight.includes("inconsistent")) {
            insightType = "Route inconsistency";
          }
          
          return insightType === value;
        },
        width: 180,
      },
    ];

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card 
              title={
                <Space>
                  <span>Stop Level Performance Statistics</span>
                  <Tooltip title="This table shows OTR performance metrics for each stop in the network. Use the filters and search to find specific stops.">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              {stopsWithIssues.length > 0 && (
                <Alert
                  message="Some stops show patterns suggesting potential positioning or timing data issues"
                  description="Use the filters to identify stops that may need attention to improve OTR performance."
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <Input.Search
                  placeholder="Search by Stop ID (TSN) or Stop Name"
                  onChange={e => setStopSearchText(e.target.value)}
                  onSearch={value => setStopSearchText(value)}
                  value={stopSearchText}
                  allowClear
                  enterButton
                  style={{ width: 400 }}
                />
                {stopSearchText && (
                  <>
                    <Text style={{ margin: '0 16px' }}>
                      Found {filteredStopStats.length} {filteredStopStats.length === 1 ? 'stop' : 'stops'} matching "{stopSearchText}"
                    </Text>
                    <Button 
                      type="link" 
                      onClick={() => setStopSearchText('')}
                      style={{ padding: '0 8px' }}
                    >
                      Reset
                    </Button>
                  </>
                )}
              </div>
              
              <Table
                dataSource={filteredStopStats}
                columns={stopColumns}
                rowKey="tsn"
                pagination={{ pageSize: 20 }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Add the missing renderDriverPerformanceView function 
  const renderDriverPerformanceView = () => {
    if (driverData.length === 0) {
      return (
        <Empty 
          description="No driver data available" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      );
    }

    // Convert driver data to format expected by OTRCharts
    const chartsData = stopData.map(record => ({
      date: record.DATE_FORMATTED || String(record.DATE_ID),
      timePeriod: OTRDataService.getTimePeriod(record.SCHEDULED_ARRIVAL_TIME),
      status: getOnTimeStatus(record.ARRIVAL_TIME_VARIANCE),
      driverId: record.DRIVER_ID,
      stopType: record.TSN_FIRST_LAST_IND || 'UNKNOWN',
      variance: record.ARRIVAL_TIME_VARIANCE
    }));

    const columns = [
      {
        title: 'Driver ID',
        dataIndex: 'driverId',
        key: 'driverId',
        sorter: (a, b) => String(a.driverId || '').localeCompare(String(b.driverId || '')),
      },
      {
        title: 'On-Time %',
        dataIndex: ['overallStats', 'onTimePercent'],
        key: 'onTimePercent',
        render: val => {
          const value = val || 0;
          let color = 'green';
          if (value < 70) color = 'blue';
          else if (value < 90) color = 'red';
          
          return (
            <Tooltip title={`${Math.round(value)}% on time`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      },
      {
        title: 'Early %',
        dataIndex: ['overallStats', 'earlyPercent'],
        key: 'earlyPercent',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.overallStats?.earlyPercent || 0) - (b.overallStats?.earlyPercent || 0),
      },
      {
        title: 'Late %',
        dataIndex: ['overallStats', 'latePercent'],
        key: 'latePercent',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.overallStats?.latePercent || 0) - (b.overallStats?.latePercent || 0),
      },
      {
        title: 'AI Insights',
        key: 'insights',
        dataIndex: 'insights',
        render: (_, record) => {
          // Generate AI insights using the ruleset
          const aiInsight = analyzeDriverWithRuleset(record, stopData);
          
          if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
            return <Tag color="default">No insights</Tag>;
          }
          
          // Create a condensed insight indicator based on the type of insight
          let insightType = "Issue";
          let tagColor = "#f50";
          
          // Categorize insights by common types for short display
          if (aiInsight.insight.includes("early departure")) {
            insightType = "Early departures";
            tagColor = "#fa8c16"; // Orange
          } else if (aiInsight.insight.includes("systematically late")) {
            insightType = "Consistently late";
            tagColor = "#1890ff"; // Blue
          } else if (aiInsight.insight.includes("inconsistency")) {
            insightType = "Inconsistent";
            tagColor = "#722ed1"; // Purple
          }
          
          // Add improvement percentage if available
          const improvementText = aiInsight.improvement ? ` +${aiInsight.improvement}%` : '';
          
          // Store insight type and improvement in record for sorting/filtering
          record.insightType = insightType;
          record.insightImprovement = aiInsight.improvement || 0;
          
          return (
            <Popover 
              content={
                <OTRInsightCard 
                  title={`Driver ${record.driverId} Insights`}
                  insight={aiInsight.insight}
                  recommendation={aiInsight.recommendation}
                  improvement={aiInsight.improvement}
                  category="driver"
                />
              } 
              title={null}
              trigger="click"
            >
              <Tag 
                color={tagColor}
                icon={<BulbOutlined />}
                style={{ cursor: 'pointer' }}
              >
                {insightType}{improvementText}
              </Tag>
            </Popover>
          );
        },
        sorter: (a, b) => {
          const insightA = analyzeDriverWithRuleset(a, stopData);
          const insightB = analyzeDriverWithRuleset(b, stopData);
          
          const improvementA = insightA?.improvement || 0;
          const improvementB = insightB?.improvement || 0;
          
          return improvementB - improvementA; // Sort by highest improvement first
        },
        filters: [
          { text: 'Early departures', value: 'Early departures' },
          { text: 'Consistently late', value: 'Consistently late' },
          { text: 'Inconsistent', value: 'Inconsistent' },
          { text: 'No issues', value: 'No insights' }
        ],
        onFilter: (value, record) => {
          const aiInsight = analyzeDriverWithRuleset(record, stopData);
          
          if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
            return value === 'No insights';
          }
          
          let insightType = "Issue";
          
          // Categorize for filtering
          if (aiInsight.insight.includes("early departure")) {
            insightType = "Early departures";
          } else if (aiInsight.insight.includes("systematically late")) {
            insightType = "Consistently late";
          } else if (aiInsight.insight.includes("inconsistency")) {
            insightType = "Inconsistent";
          }
          
          return insightType === value;
        },
        width: 180
      }
    ];

    const driverDataWithInsights = driverData.map(record => {
      const aiInsight = analyzeDriverWithRuleset(record, stopData);
      let insightType = "Issue";
      if (aiInsight?.insight?.includes("early departure")) insightType = "Early departures";
      else if (aiInsight?.insight?.includes("systematically late")) insightType = "Consistently late";
      else if (aiInsight?.insight?.includes("inconsistency")) insightType = "Inconsistent";
      else if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) insightType = "No insights";
      return {
        ...record,
        aiInsight,
        insightType,
        insightImprovement: aiInsight?.improvement || 0,
      };
    });

    return (
      <div>
        <Card title="Driver Performance">
          <Table
            columns={columns}
            dataSource={driverDataWithInsights}
            rowKey="driverId"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      </div>
    );
  };

  // Create a function to render global filters
  const renderGlobalFilters = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Row style={{ width: '100%' }} justify="space-between" align="middle">
          <Col flex="320px">
            <OTRDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              selectedPreset={selectedDatePreset}
              onPresetChange={handleDatePresetChange}
            />
          </Col>
          
          <Col flex="auto" style={{ display: 'flex', gap: '8px', marginLeft: '16px', marginRight: '16px' }}>
            <Select
              placeholder="Route"
              style={{ width: '110px', minWidth: '110px' }}
              value={selectedRoute}
              onChange={setSelectedRoute}
              allowClear
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
            >
              {filterOptions.routes.map(route => (
                <Option key={route} value={route}>{route}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="Driver"
              style={{ width: '110px', minWidth: '110px' }}
              value={selectedDriver}
              onChange={setSelectedDriver}
              allowClear
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
            >
              {filterOptions.drivers.map(driver => (
                <Option key={driver} value={driver}>{driver}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="Depot"
              style={{ width: '110px', minWidth: '110px' }}
              value={selectedDepot}
              onChange={setSelectedDepot}
              allowClear
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
            >
              {filterOptions.depots.map(depot => (
                <Option key={depot} value={depot}>{depot}</Option>
              ))}
            </Select>
            
            <Select
              placeholder="Contract"
              style={{ width: '110px', minWidth: '110px' }}
              value={selectedContract}
              onChange={setSelectedContract}
              allowClear
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
            >
              {filterOptions.contracts.map(contract => (
                <Option key={contract} value={contract}>{contract}</Option>
              ))}
            </Select>
          </Col>
          
          <Col flex="auto" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <Tag.CheckableTag
                checked={selectedStopTypes.includes('ALL')}
                onChange={checked => {
                  if (checked) {
                    setSelectedStopTypes(['ALL']);
                  } else if (selectedStopTypes.length === 1 && selectedStopTypes[0] === 'ALL') {
                    setSelectedStopTypes(['F']); 
                  }
                }}
                style={{ 
                  borderRadius: '6px', 
                  padding: '5px 10px',
                  height: '32px',
                  lineHeight: '22px',
                  fontSize: '14px',
                  backgroundColor: selectedStopTypes.includes('ALL') ? '#1890ff' : undefined,
                  color: selectedStopTypes.includes('ALL') ? 'white' : undefined,
                  border: selectedStopTypes.includes('ALL') ? 'none' : '1px solid #d9d9d9',
                  cursor: 'pointer',
                  marginRight: '4px'
                }}
              >
                All Stops
              </Tag.CheckableTag>
              
              <Tag.CheckableTag
                checked={selectedStopTypes.includes('F')}
                onChange={checked => {
                  let newSelection = [...selectedStopTypes];
                  newSelection = newSelection.filter(t => t !== 'ALL');
                  
                  if (checked) {
                    if (!newSelection.includes('F')) newSelection.push('F');
                  } else {
                    newSelection = newSelection.filter(t => t !== 'F');
                  }
                  
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }}
                style={{ 
                  borderRadius: '6px', 
                  padding: '5px 10px',
                  height: '32px',
                  lineHeight: '22px',
                  fontSize: '14px',
                  backgroundColor: selectedStopTypes.includes('F') ? '#1890ff' : undefined,
                  color: selectedStopTypes.includes('F') ? 'white' : undefined,
                  border: selectedStopTypes.includes('F') ? 'none' : '1px solid #d9d9d9',
                  cursor: 'pointer',
                  marginRight: '4px'
                }}
              >
                First
              </Tag.CheckableTag>
              
              <Tag.CheckableTag
                checked={selectedStopTypes.includes('M')}
                onChange={checked => {
                  let newSelection = [...selectedStopTypes];
                  newSelection = newSelection.filter(t => t !== 'ALL');
                  
                  if (checked) {
                    if (!newSelection.includes('M')) newSelection.push('M');
                  } else {
                    newSelection = newSelection.filter(t => t !== 'M');
                  }
                  
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }}
                style={{ 
                  borderRadius: '6px', 
                  padding: '5px 10px',
                  height: '32px',
                  lineHeight: '22px',
                  fontSize: '14px',
                  backgroundColor: selectedStopTypes.includes('M') ? '#1890ff' : undefined,
                  color: selectedStopTypes.includes('M') ? 'white' : undefined,
                  border: selectedStopTypes.includes('M') ? 'none' : '1px solid #d9d9d9',
                  cursor: 'pointer',
                  marginRight: '4px'
                }}
              >
                Mid
              </Tag.CheckableTag>
              
              <Tag.CheckableTag
                checked={selectedStopTypes.includes('L')}
                onChange={checked => {
                  let newSelection = [...selectedStopTypes];
                  newSelection = newSelection.filter(t => t !== 'ALL');
                  
                  if (checked) {
                    if (!newSelection.includes('L')) newSelection.push('L');
                  } else {
                    newSelection = newSelection.filter(t => t !== 'L');
                  }
                  
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }}
                style={{ 
                  borderRadius: '6px', 
                  padding: '5px 10px',
                  height: '32px',
                  lineHeight: '22px',
                  fontSize: '14px',
                  backgroundColor: selectedStopTypes.includes('L') ? '#1890ff' : undefined,
                  color: selectedStopTypes.includes('L') ? 'white' : undefined,
                  border: selectedStopTypes.includes('L') ? 'none' : '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
              >
                Last
              </Tag.CheckableTag>
            </div>
            
            {/* <Button 
              type="primary" 
              icon={<ImportOutlined />}
              onClick={() => setShowImporter(true)}
              style={{ marginLeft: '12px' }}
            >
              Import Data
            </Button> */}
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div>
      {/* Header Card with Title and Filters */}
      <Card 
        title="OTR Dashboard" 
        extra={<Text type="secondary">On-Time Running Performance Analysis</Text>}
        style={{ marginBottom: '16px' }}
      >
        {/* Add global filters */}
        {renderGlobalFilters()}
      </Card>
      
      {/* Dashboard Content Card */}
      <Card>        
        <Tabs 
          activeKey={activeTab}
          onChange={key => {
            setActiveTab(key);
            if (key === 'trip' && viewLevel !== VIEW_LEVELS.TRIP_AGGREGATE) {
              // Reset to level 1 when switching to trip tab
              setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
              setSelectedTrip(null);
              setSelectedDate(null);
              setFilteredData(tripData);
            }
          }}
          size="large"
          tabBarStyle={{ marginBottom: '20px' }}
        >
          <TabPane 
            tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><LineChartOutlined style={{ marginRight: '12px' }} />Performance Overview</span>} 
            key="overview"
          >
            {activeTab === 'overview' && renderContent()}
          </TabPane>
          
          <TabPane 
            tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><CarOutlined style={{ marginRight: '12px' }} />Trip Performance</span>} 
            key="trip"
          >
            {activeTab === 'trip' && (
              <div>
                {viewLevel > VIEW_LEVELS.TRIP_AGGREGATE && renderBreadcrumb()}
                {renderContent()}
              </div>
            )}
          </TabPane>
        
          <TabPane 
            tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><ClockCircleOutlined style={{ marginRight: '12px' }} />Daily Performance</span>} 
            key="dailyOverview"
          >
            {activeTab === 'dailyOverview' && renderContent()}
          </TabPane>              
          
          <TabPane 
            tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><HeatMapOutlined style={{ marginRight: '12px' }} />Stop Performance</span>} 
            key="stop"
          >
            {activeTab === 'stop' && (
              <div>
                {viewLevel === VIEW_LEVELS.STOP_DETAIL && renderBreadcrumb()}
                {renderContent()}
              </div>
            )}
          </TabPane>
          
          {/* <TabPane 
            tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><BarChartOutlined style={{ marginRight: '12px' }} />Driver Performance</span>} 
            key="driver"
          >
            {activeTab === 'driver' && renderContent()}
          </TabPane> */}
        </Tabs>
      </Card>
    </div>
  );
};

export default OTRDashboard; 